import { Checkbox } from "@omnia/shared/react/checkbox";
import { ITown } from "@omnia/shared/town";
import { formatToReadable } from "@omnia/shared/utils/format";
import { HoldingData } from "./types";
import { FormGroupTextArea, FormGroupTextInput, TextInput } from "@omnia/shared/react/TextInput";
import { FormGroupSelect, FormGroupSelectNumber, SelectNumber } from "@omnia/shared/react/Select";
import { Msg } from "@omnia/shared/react/Msg";
import { Typeahead2 } from "@omnia/shared/react/Typeahead2/Typeahead2";
import { useMemo, useState } from "react";
import { FormGroupNumericInput } from "@omnia/shared/react/NumericInput";
import { isHoldingValid } from "./validation";

interface Props {
    data: HoldingData;
    onUpdate: (newData: Partial<HoldingData>) => void;
}

export function HoldingEditTabHolding({ data, onUpdate }: Props) {
    const [townQuery, setTownQuery] = useState("");

    const businessOptions = useMemo(() => {
        return (data.businesses.sort((a, b) => a.Name.localeCompare(b.Name)).map((b) => ([b.BusinessId, b.Name] satisfies [number, string])));
    }, [data?.businesses]);

    const regionOptions = useMemo(() => {
        return data.regions?.sort((a, b) => a?.text?.localeCompare(b?.text)).map(r => ([r.value, r.text] satisfies [number, string]))
    }, [data?.regions]);

    return <>
    {data &&
    <div className="row">
        <div className="col-md-6">
            <FormGroupTextInput className="required"
                label={(<Msg>Trading Name</Msg>)}
                placeholder="Trading Name"
                autoComplete="---"
                required={true}
                value={data.holdingTradingName ?? ""}
                setValue={s => onUpdate({ holdingTradingName: s })}
                invalid={!isHoldingValid("holdingTradingName", data)}
            />
            
            {data.enableBusiness &&
                <div className="form-group">
                    <label><Msg>Business</Msg></label>
                    {data.canSetBusiness ? 
                        <SelectNumber
                            placeholder="Select a business"
                            autoComplete="---"
                            defaultLabel=""
                            options={businessOptions}
                            value={data.business?.BusinessId}
                            setValue={id => onUpdate({ business: data.businesses.find(b => b.BusinessId === id) })}
                        />
                        : 
                        <TextInput
                            disabled={true}
                            value={data.business?.Name}
                        />
                    }
                </div>
            }

            {data.enablePostTown &&
                <div className={`form-group required ${isHoldingValid('town', data) ? '' : 'invalid'}`}>
                    <label><Msg>Post Town</Msg></label>
                    <Typeahead2<ITown>
                        placeholder="Select a town"
                        source={{items: data.towns}}
                        keySelector={t => t.TownId}
                        textSelector={t => t.Name}
                        text={townQuery}
                        setText={setTownQuery}
                        selected={data.towns?.find(t => t.TownId === data.town?.TownId)}
                        setSelected={t => onUpdate({ town: t })}
                        matchContent={t => (<span className="text-muted">{t.Name}</span>)}
                    />
                </div>
            }

            {!!data.regions && 
                <FormGroupSelectNumber className="required"
                    label={<Msg>Region</Msg>}
                    autoComplete="---"
                    disabled={!data.canChangeRegion}
                    options={regionOptions}
                    value={data.region}
                    setValue={id => onUpdate({ region: id })}
                    invalid={!isHoldingValid('region', data)}
                />
            }

            {data.enableNvz && 
                <div className="form-group">
                    <div className={`checkbox nvz-checkbox ${!data.nvz ? 'not-in-nvz' : ''}`}>
                        <Checkbox 
                            disabled={!data.canChangeNvz}
                            checked={data.nvz}
                            setChecked={c => onUpdate({ nvz: c })}
                        />
                        <Msg>Nitrate vulnerable zone</Msg>
                        <br />
                        <a href={data.nvzLink} target="_blank"><Msg>Not Sure? Check here</Msg></a>
                    </div>
                </div>
            }

            {!data.isNew &&
                <div className="year-data">
                    <header>{data.year}</header>

                    {data.hasPreviousYear &&
                        <FormGroupNumericInput label={<><Msg>Total area</Msg> {data.year -1} (ha)</>}
                            value={data.totalAreaPreviousYear}
                            disabled={true}
                        />
                    }

                    <FormGroupNumericInput label={`Total area ${data.hasPreviousYear ? data.year : ''} (calculated: ${formatToReadable(data.totalAreaCalculated)}ha)`}
                        min={0}
                        value={data.totalArea}
                        setValue={n => onUpdate({ totalArea: n })}
                        invalid={!isHoldingValid('totalArea', data)}
                    />

                    <div className="form-group">
                        <Checkbox 
                            checked={data.grasslandDerogation} 
                            setChecked={c => onUpdate({ grasslandDerogation: c })} />
                        <Msg>Grassland Derogation</Msg>
                    </div>

                    {data.grasslandDerogation &&
                        <FormGroupTextInput className={data.grasslandDerogation ? 'required' : ''}
                            label={<Msg>Grassland Derogation number</Msg>}
                            autoComplete="---"
                            value={data.grasslandDerogationNumber ?? ""}
                            setValue={s => onUpdate({ grasslandDerogationNumber: s })}
                            required={data.grasslandDerogation}
                            invalid={!isHoldingValid('grasslandDerogationNumber', data)}
                        />
                    }
                </div>
            }
           
        </div>

        <div className="col-md-6">
            <FormGroupTextInput
                label={<Msg>Account Number</Msg>}
                autoComplete="---"
                placeholder="Account Number"
                disabled={!data.canEditAccountNumber}
                maxLength={20}
                value={data.holdingAccountNumber ?? ""}
                setValue={s => onUpdate({ holdingAccountNumber: s })}
            />

            {data.enableReportingOption &&
                <>
                    <Checkbox 
                        checked={data.includeInReports}
                        setChecked={c => onUpdate({ includeInReports: c })}
                    />
                    <Msg>Include holding in my Power BI report</Msg>
                </>

            }
            
            <FormGroupSelectNumber className="required"
                label={<Msg>Starting Year</Msg>}
                options={data.startingCropYears.map(y => ([y.Year, y.Year.toString()]))}
                value={data.firstCroppingYear}
                setValue={n => onUpdate({ firstCroppingYear: n })}
                invalid={!isHoldingValid('firstCroppingYear', data)}
            />

            <FormGroupTextArea 
                label={<Msg>Address</Msg>}
                rows={4}
                maxRows={4}
                value={data.holdingAddress ?? ""}
                setValue={s => onUpdate({ holdingAddress: s })}
            />

            <FormGroupSelect className="required"
                label={<Msg>PK model</Msg>}
                disabled={!data.canSelectPkModel}
                options={['ADAS', 'SRUC'].map(s => ([s, s]))}
                value={data.pkModel}
                setValue={s => onUpdate({ pkModel: s})}
                invalid={!isHoldingValid('pkModel', data)}
            />

            {(data.pkModel === 'SRUC') &&
                <FormGroupSelectNumber label={<Msg>Default P sorp capacity</Msg>}
                    options={[1, 2, 3].map(n => ([n, n.toString()]))}
                    value={data.holdingPSorpCapacity}
                    setValue={n => onUpdate({ holdingPSorpCapacity: n})}
                />
            }

        </div>
    </div>
}
    </>
}